/**
 * The owner's approval queue (src/aithora/v3/approvals.ts) and the Settings change
 * requests decided from it (src/aithora/v3/settings.ts). Served; every route carries the
 * `real` flag. Only the owner (Andrew) or the deputy he names may read or decide; anyone
 * else is refused (403).
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type { PolicyRequest } from './policy-types';

const A = AITHORA_PREFIX;

/** A person an Operator created during onboarding, waiting for the owner. */
export interface PendingPerson {
  id: string;
  subjectType: 'user' | 'invite';
  subjectId: string;
  facility: { id: string; name: string | null };
  email: string | null;
  name: string | null;
  role: string;
  source: string;
  requestedBy: { userId: string; name: string | null; role: string | null } | null;
  requestedAt: string | null;
  status: string;
}

/** A Facility Admin's request to change a Settings section (listSettingsChangeRequests). */
export interface SettingsChangeRequest {
  id: string;
  facilityId: string;
  facilityName: string | null;
  section: string;
  proposed: unknown;
  before: unknown;
  status: 'PENDING' | 'APPROVED' | 'DECLINED';
  requestedBy: string | null;
  requestedAt: string | null;
}

export interface ApprovalQueue {
  real: true;
  /** 'owner' (Andrew), 'deputy' (the approver Andrew named), or 'viewer' (an Operator: may look, never decides). */
  as: 'owner' | 'deputy' | 'viewer';
  counts: { people: number; policyRequests: number; settingsRequests: number };
  people: PendingPerson[];
  policyRequests: PolicyRequest[];
  settingsRequests: SettingsChangeRequest[];
}

/** GET /approvals: everything waiting for the owner. The owner or the deputy; an Operator reads it as 'viewer'; others 403. */
export const approvalsQueue = route<ApprovalQueue>()('GET', `${A}/approvals`, { honest: true });
/** POST /approvals/people/:id/approve {reason?}. */
export const approvalPersonApprove = route<{ real: true; ledgerEntryId: string | null }, void, { reason?: string }>()(
  'POST', `${A}/approvals/people/:id/approve`, { honest: true });
/** POST /approvals/people/:id/decline {reason}: a reason is required. */
export const approvalPersonDecline = route<{ real: true; ledgerEntryId: string | null }, void, { reason: string }>()(
  'POST', `${A}/approvals/people/:id/decline`, { honest: true });
/** POST /settings/change-requests/:id/approve {note?}: re-checked against today's values first. */
export const settingsRequestApprove = route<{ real: true; id: string; status: string }, void, { note?: string }>()(
  'POST', `${A}/settings/change-requests/:id/approve`, { honest: true });
/** POST /settings/change-requests/:id/decline {note?}. */
export const settingsRequestDecline = route<{ real: true; id: string; status: string }, void, { note?: string }>()(
  'POST', `${A}/settings/change-requests/:id/decline`, { honest: true });

/** Who the deputy approver is (GET /approvals/deputy). None by default: then only Andrew approves. */
export interface ApprovalDeputy {
  real: true;
  deputy: { userId: string; name: string | null; email: string | null; role: string } | null;
  eligibleRoles: string[];
  mayChange: boolean;
}

/** GET /approvals/deputy: Aithora platform staff and the deputy may look; others 403. */
export const approvalsDeputy = route<ApprovalDeputy>()('GET', `${A}/approvals/deputy`, { honest: true });
